import React, { useContext, useState, useEffect } from 'react';
import { SnackbarContext } from '../../../../state/SnackbarContextProvider';
import {
  TextField,
  Button,
  Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress
} from '@mui/material';
import useAction from '../../../../utils/useAction';
import { updateSchoolTeacher } from '../../../../actions/user.actions';
import { useTranslation } from 'react-i18next';
import { Teacher } from '@klab-berlin/api-sdk/lib/types/responses/Teacher';
import { useSelector } from 'react-redux';
import { selectSelectedSchoolId } from '../../../../reducers/user.selectors';

interface UpdateTeacherProps {
  teacher: Teacher | null;
  open: boolean;
  onClose: () => void;
  onTeacherUpdated: () => void;
}

const UpdateSchoolTeacher: React.FC<UpdateTeacherProps> = ({ teacher, open, onClose, onTeacherUpdated }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: ''
  });

  const [loading, setLoading] = useState(false);
  const updateSchoolTeacherAction = useAction(updateSchoolTeacher);
  const { t } = useTranslation();
  const { openSnackbar } = useContext(SnackbarContext);
  const selectedSchoolId = useSelector(selectSelectedSchoolId);
  
  // Populate form when teacher changes or dialog opens
  useEffect(() => {
    if (teacher && open) {
      const { firstName, lastName } = teacher;
      setFormData({
        firstName: firstName || '',
        lastName: lastName || '',
        email: teacher.email || ''
      });
    }
  }, [teacher, open]);

  const showError = (message: string) => {
    openSnackbar({
      type: 'error',
      message,
    });
  };

  const showInfo = (message: string) => {
    openSnackbar({
      type: 'success',
      message,
    });
  };

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const clearForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: ''
    });
  };

  const handleClose = () => {
    setLoading(false);
    clearForm();
    onClose();
  };

  const handleSubmit = async () => {
    if (!teacher) {
      showError(t('No teacher selected'));
      return;
    }

    if (!selectedSchoolId) {
      showError(t('No school selected'));
      return;
    }

    // Validate required fields
    if (!formData.firstName || !formData.lastName || !formData.email) {
      showError(t('All fields are required'));
      return;
    }

    // Validate email format
    if (!validateEmail(formData.email)) {
      showError(t('Invalid email address'));
      return;
    }
    setLoading(true);

    try {
      const result = await updateSchoolTeacherAction(
        teacher.userId,
        selectedSchoolId,
        formData.firstName,
        formData.lastName,
        formData.email
      );

      if (!result.error) {
        showInfo(t('Teacher updated successfully'));
        onTeacherUpdated();
        handleClose();
      } else {
        const message = t(result.error.data.message) || t('Failed to update teacher');
        showError(message);
      }
    } catch (err: any) {
      showError(t(err.message) || t('Failed to update teacher'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>{t('Update Teacher')}</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          size='small'
          label={t('Vorname')}
          value={formData.firstName}
          onChange={handleInputChange('firstName')}
          variant='standard'
          required
          inputProps={{ 'aria-label': 'Vorname' }}
        />
        <TextField
          fullWidth
          size='small'
          label={t('Nachname')}
          value={formData.lastName}
          onChange={handleInputChange('lastName')}
          variant='standard'
          required
          inputProps={{ 'aria-label': 'Nachname' }}
        />
        <TextField
          fullWidth
          size='small'
          label={t('E-Mail-Adresse')}
          value={formData.email}
          onChange={handleInputChange('email')}
          variant='standard'
          required
          inputProps={{ 'aria-label': 'E-Mail-Adresse' }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t('Cancel')}</Button>
        <Button
          onClick={handleSubmit}
          startIcon={loading && <CircularProgress size={20} color='inherit' />}
          disabled={loading}
          variant='contained'
          color='success'
        >
          {loading ? t('Updating...') : t('Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateSchoolTeacher;
